/**
 * ErpMappingsTab — zakładka „Mapowania" ekranu Integracja ERP.
 *
 * Zbiera sekcje mapowań pól (te same co rozwijane wiersze w IntegrationsTab) w jednym
 * miejscu — po jednej `FieldMappingsSection` per integracja. Przełącznik encji
 * (kontrahent / produkt / faktura) `FieldMappingsSection` obsługuje wewnętrznie, więc
 * przekazujemy jedynie `integrationId`. Zmiana czysto frontendowa, bez backendu.
 */

import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { Spinner } from '@/components/Spinner';
import { api } from '@/lib/api';

import { FieldMappingsSection } from './FieldMappingsSection';
import { type ErpIntegrationDto } from './IntegrationsTab';

export function ErpMappingsTab({
  integrationId,
  erpSystem,
}: { integrationId?: string; erpSystem?: string; connectionMode?: string } = {}) {
  const { t } = useTranslation();

  const listQ = useQuery({
    queryKey: ['erp-integrations', 'mappings'],
    queryFn: async () => {
      const res = await api.get<{ integrations: ErpIntegrationDto[] }>('/erp-integrations', {
        params: {},
      });
      return res.data.integrations;
    },
    enabled: !integrationId,
  });

  // Widok szczegółu: sekcja mapowań pól + mapowań typów dokumentów dla wskazanej integracji.
  if (integrationId) {
    return (
      <div className="space-y-6">
        <FieldMappingsSection integrationId={integrationId} erpSystem={erpSystem} />
      </div>
    );
  }

  if (listQ.isLoading) {
    return (
      <div className="flex justify-center p-10">
        <Spinner size="lg" />
      </div>
    );
  }

  const integrations = (listQ.data ?? []).filter((it) => it.gcrecord == null);

  if (integrations.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border-secondary p-10 text-center text-sm text-text-secondary">
        {t('tenant.settings.integrations.mappings.empty')}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {integrations.map((it) => (
        <section key={it.id} className="space-y-4">
          <h3 className="mb-2 text-sm font-medium text-text-primary">{it.name}</h3>
          <FieldMappingsSection integrationId={it.id} erpSystem={it.erpSystem} />
        </section>
      ))}
    </div>
  );
}
